import styled from 'styled-components';
import { MapPin, Phone, User, Calendar, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import type { LocationDetail } from '../../services/api/locationService';

interface OverviewTabProps {
  location: LocationDetail;
  onEditClick: () => void;
}

// ==================== Styled Components ====================

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const InfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;
`;

const InfoIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 1px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const InfoLabel = styled.dt`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  font-style: italic;
`;

// ==================== Helpers ====================

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const buildAddress = (loc: LocationDetail): string =>
  [loc.street, loc.city, loc.state, loc.zip, loc.country].filter(Boolean).join(', ');

// ==================== Component ====================

export const OverviewTab = ({ location, onEditClick }: OverviewTabProps) => (
  <Grid>
    <Card>
      <CardHeader>
        <CardTitle>Center Information</CardTitle>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Pencil size={14} />}
          onClick={onEditClick}
        >
          Edit
        </Button>
      </CardHeader>
      <InfoList>
        <InfoRow>
          <InfoIcon>
            <MapPin />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Address</InfoLabel>
            <InfoValue>{buildAddress(location)}</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <InfoIcon>
            <Phone />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Phone</InfoLabel>
            {location.phone ? (
              <InfoValue>{location.phone}</InfoValue>
            ) : (
              <EmptyValue>Not provided</EmptyValue>
            )}
          </InfoContent>
        </InfoRow>
      </InfoList>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Administrative</CardTitle>
      </CardHeader>
      <InfoList>
        <InfoRow>
          <InfoIcon>
            <User />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Created By</InfoLabel>
            {location.createdBy ? (
              <InfoValue>
                {location.createdBy.firstName} {location.createdBy.lastName}
              </InfoValue>
            ) : (
              <EmptyValue>Unknown</EmptyValue>
            )}
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <InfoIcon>
            <Calendar />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Created</InfoLabel>
            <InfoValue>{formatDate(location.createdAt)}</InfoValue>
          </InfoContent>
        </InfoRow>

        <InfoRow>
          <InfoIcon>
            <Calendar />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>Last Updated</InfoLabel>
            <InfoValue>{formatDate(location.updatedAt)}</InfoValue>
          </InfoContent>
        </InfoRow>
      </InfoList>
    </Card>
  </Grid>
);
